import { createLogger } from "../helper/logger.js";
import { pickRandomKey, runLimited } from "./sound-utils.js";
//#region src/sound/SoundService.ts
/**
* SoundService — Web-Audio playback of a registered sound inventory.
*
* What it fixes compared to the usual ad-hoc implementation:
* - AudioContext is created lazily (never at module evaluation) and unlocked
*   on the first user gesture (`resume()`), so sounds triggered later outside
*   a gesture (animation callbacks) are audible.
* - One master GainNode: `setVolume()` / `setMuted()` really apply.
* - Preload with a concurrency limit; every failure is reported, none throws.
* - Each play gets its own source + gain and disconnects itself on end.
* - Playing a sound that is not (yet) loaded is a silent no-op with a debug
*   log — never an exception.
*
* Store-free: the app decides whether preload progress becomes an action.
*
* @example
* ```typescript
* soundService.register([{ key: "coin", url: "/sounds/coin.mp3" }]);
* await soundService.preload(["coin"]);          // e.g. as a non-blocking init task
* soundService.play("coin", { volume: 0.5 });
* soundService.playRandom("cardShove", 4);       // cardShove1 … cardShove4
* ```
*/
var logger = createLogger("SoundService");
var clamp01 = (value) => Math.min(1, Math.max(0, value));
var SoundService = class {
	constructor() {
		this._definitions = /* @__PURE__ */ new Map();
		this._buffers = /* @__PURE__ */ new Map();
		this._pending = /* @__PURE__ */ new Map();
		this._volume = 1;
		this._muted = false;
		this._unlockInstalled = false;
	}
	/** Web Audio available? (SSR/tests: false — every call becomes a no-op.) */
	get isSupported() {
		return typeof window !== "undefined" && "AudioContext" in window;
	}
	get volume() {
		return this._volume;
	}
	get muted() {
		return this._muted;
	}
	/** Make sounds known; re-registering a key replaces its definition. */
	register(definitions) {
		for (const definition of definitions) this._definitions.set(definition.key, definition);
	}
	isLoaded(key) {
		return this._buffers.has(key);
	}
	/**
	* Fetch + decode the given keys, at most `concurrency` at a time.
	* Resolves always; failures are listed, not thrown.
	*/
	async preload(keys, concurrency = 4) {
		const context = this._ensureContext();
		if (!context) return {
			loaded: [],
			failed: [...keys]
		};
		const results = await runLimited(keys, concurrency, (key) => this._load(context, key));
		const loaded = [];
		const failed = [];
		results.forEach((result, index) => {
			const key = keys[index];
			if (result.status === "fulfilled") loaded.push(key);
			else {
				failed.push(key);
				logger.warn(`Sound "${key}" not loaded`, result.reason);
			}
		});
		return {
			loaded,
			failed
		};
	}
	/** Play a loaded sound; silent no-op if unknown, not loaded or unsupported. */
	play(key, options = {}) {
		const context = this._context;
		const buffer = this._buffers.get(key);
		if (!context || !buffer || !this._master) {
			logger.debug(`Sound "${key}" skipped (not loaded)`);
			return;
		}
		if (context.state === "suspended") context.resume();
		const source = context.createBufferSource();
		source.buffer = buffer;
		source.loop = options.loop ?? false;
		const gain = context.createGain();
		gain.gain.value = clamp01(options.volume ?? this._definitions.get(key)?.volume ?? 1);
		source.connect(gain).connect(this._master);
		source.onended = () => {
			source.disconnect();
			gain.disconnect();
		};
		source.start();
		let stopped = false;
		return { stop: () => {
			if (stopped) return;
			stopped = true;
			source.stop();
		} };
	}
	/** Play one of `prefix1 … prefixCount` at random (variant sounds). */
	playRandom(prefix, count, options) {
		return this.play(pickRandomKey(prefix, count), options);
	}
	/** Master volume 0–1 (applies to every play, also running ones). */
	setVolume(value) {
		this._volume = clamp01(value);
		this._applyMasterGain();
	}
	setMuted(muted) {
		this._muted = muted;
		this._applyMasterGain();
	}
	_ensureContext() {
		if (!this.isSupported) return;
		if (!this._context) {
			this._context = new AudioContext();
			this._master = this._context.createGain();
			this._master.connect(this._context.destination);
			this._applyMasterGain();
			this._installUnlock();
		}
		return this._context;
	}
	/**
	* Autoplay policy: the context starts `suspended` until a user gesture.
	* Resume on the first pointer/key gesture (capture phase, so it runs
	* before any app handler that might already want to play).
	*/
	_installUnlock() {
		if (this._unlockInstalled) return;
		this._unlockInstalled = true;
		const unlock = () => {
			if (this._context?.state === "suspended") this._context.resume();
		};
		for (const type of ["pointerdown", "keydown"]) window.addEventListener(type, unlock, {
			once: true,
			passive: true,
			capture: true
		});
	}
	_applyMasterGain() {
		if (this._master) this._master.gain.value = this._muted ? 0 : this._volume;
	}
	_load(context, key) {
		const cached = this._buffers.get(key);
		if (cached) return Promise.resolve(cached);
		const pending = this._pending.get(key);
		if (pending) return pending;
		const definition = this._definitions.get(key);
		if (!definition) return Promise.reject(/* @__PURE__ */ new Error(`Sound "${key}" is not registered`));
		const load = (async () => {
			try {
				const response = await fetch(definition.url);
				if (!response.ok) throw new Error(`${definition.url}: HTTP ${response.status}`);
				const buffer = await context.decodeAudioData(await response.arrayBuffer());
				this._buffers.set(key, buffer);
				return buffer;
			} finally {
				this._pending.delete(key);
			}
		})();
		this._pending.set(key, load);
		return load;
	}
};
var soundService = new SoundService();
//#endregion
export { SoundService, soundService };
